import React, { useState } from 'react';
import { SearchIcon, ArrowDown, PlusIcon, FilterIcon, CloseIcon } from '../../../public/icons';
import Pagination from '../../shared/Pagination';

// Mobile Filter Modal
const MobileFiltersModal = ({ isOpen, onClose, searchQuery, setSearchQuery, statusFilter, setStatusFilter, statusFilters }) => {
  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={onClose} />
      <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl z-50 p-5 max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-lg font-medium text-[#1C2A46]">Filters</h3>
          <button onClick={onClose} className="p-1">
            <CloseIcon size={20} />
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                <SearchIcon size={16} className="text-[#6B635D]" />
              </div>
              <input
                type="text"
                placeholder="Search promo codes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full p-2.5 pl-10 pr-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#D4A574] text-sm"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <div className="grid grid-cols-2 gap-2">
              {statusFilters.map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => {
                    setStatusFilter(filter.id);
                    onClose();
                  }}
                  className={`px-3 py-2 text-sm rounded border transition-colors ${
                    statusFilter === filter.id
                      ? 'bg-[#D2A146] text-white border-[#D2A146]'
                      : 'bg-white text-[#1C2A46] border-[#DBDBDB] hover:border-[#D2A146]'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        <div className="flex gap-3 mt-6">
          <button 
            onClick={() => {
              setSearchQuery('');
              setStatusFilter('all');
              onClose();
            }}
            className="flex-1 py-3 border border-gray-300 text-gray-700 font-medium rounded hover:bg-gray-50 transition-colors"
          >
            Clear All
          </button>
          <button 
            onClick={onClose}
            className="flex-1 py-3 bg-[#D2A146] text-white font-medium rounded hover:bg-[#C49660] transition-colors"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </>
  );
};

const DiscountPromoTab = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const promoCodes = [
    { id: 1, code: 'LEGACY20', type: 'Percentage', value: '20%', usage: '2/10', status: 'Expired' },
    { id: 2, code: 'NEWCUST10', type: 'Percentage', value: '10%', usage: '25/50', status: 'Expired' },
    { id: 3, code: 'LEGACY20', type: 'Percentage', value: '5%', usage: '10/20', status: 'Active' },
    { id: 4, code: 'LEGACY20', type: 'Fixed', value: '₦5,000', usage: '3/10', status: 'Active' },
    { id: 5, code: 'GIFT5000', type: 'Fixed', value: '₦5,000', usage: '30/100', status: 'Active' },
    { id: 6, code: 'LEGACY20', type: 'Percentage', value: '2%', usage: '2/5', status: 'Expired' }
  ];

  const statusFilters = [
    { id: 'all', label: 'All status' },
    { id: 'active', label: 'Active' },
    { id: 'expired', label: 'Expired' }
  ];

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Active': {
        textColor: 'text-[#2F8F5B]',
        bgColor: 'bg-[#E7F4ED]',
        dotColor: 'bg-[#2F8F5B]'
      },
      'Expired': {
        textColor: 'text-[#6B635D]',
        bgColor: 'bg-[#F3F4F6]',
        dotColor: 'bg-[#6B635D]'
      }
    };

    const config = statusConfig[status] || statusConfig['Active'];

    return (
      <span className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 text-[10px] sm:text-xs leading-5 font-semibold rounded-full ${config.bgColor} w-fit whitespace-nowrap`}>
        <span className={`h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full ${config.dotColor}`} />
        <span className={config.textColor}>{status}</span>
        <ArrowDown size={10} className={config.textColor} />
      </span>
    );
  };

  const filteredPromoCodes = promoCodes.filter(promo => {
    const matchesSearch = promo.code.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
                         promo.status.toLowerCase() === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div>
      <div className="bg-white rounded-sm p-4 sm:p-6">
        {/* Desktop view */}
        <div className="hidden md:flex items-center justify-between mb-6">
          <h2 className="text-lg sm:text-xl font-medium text-[#0E1628]">Promo & Discount Codes</h2>
          
          <div className="flex gap-3 lg:gap-4 items-center">
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                <SearchIcon size={18} className="text-[#6B635D]" />
              </div>
              <input
                type="text"
                placeholder="Search promo codes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="p-2 pl-9 pr-3 border w-56 lg:w-64 border-[#DBDBDB] bg-transparent placeholder:text-[#6B635D] placeholder:text-xs lg:placeholder:text-sm focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent rounded"
              />
            </div>

            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="p-2 pl-3 pr-7 border w-32 lg:w-36 border-[#DBDBDB] bg-transparent text-[#1C2A46] text-xs lg:text-sm appearance-none focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent rounded"
              >
                {statusFilters.map(filter => (
                  <option key={filter.id} value={filter.id}>
                    {filter.label}
                  </option>
                ))}
              </select>
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <ArrowDown size={14} className="text-[#6B635D]" />
              </div>
            </div>

            <button className="flex items-center gap-2 px-3 lg:px-4 py-2 bg-[#D4A574] text-white font-medium rounded hover:bg-[#C49660] transition-colors text-xs lg:text-sm whitespace-nowrap">
              <PlusIcon className="w-3 h-3 sm:w-4 sm:h-4" />
              Create Discount
            </button>
          </div>
        </div>

        {/* Mobile view */}
        <div className="md:hidden">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-medium text-[#0E1628]">Promo & Discount Codes</h2>
            <button 
              onClick={() => setMobileFiltersOpen(true)}
              className="flex items-center gap-2 px-3 py-2 border border-[#DBDBDB] rounded-sm text-sm"
            >
              <FilterIcon size={14} />
              Filters
            </button>
          </div>
          
          <div className="relative mb-3">
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
              <SearchIcon size={16} className="text-[#6B635D]" />
            </div>
            <input
              type="text"
              placeholder="Search promo codes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full p-2.5 pl-10 pr-3 border border-[#DBDBDB] bg-transparent placeholder:text-[#6B635D] placeholder:text-xs focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent rounded"
            />
          </div>

          <button className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#D4A574] text-white font-medium rounded hover:bg-[#C49660] transition-colors text-sm">
            <PlusIcon className="w-4 h-4" />
            Create Discount
          </button>
        </div>

        {/* Table - Horizontal scroll on mobile */}
        <div className="overflow-x-auto mt-4 sm:mt-6">
          <table className="min-w-[550px] md:min-w-full divide-y divide-[#DBDBDB]">
            <thead className="bg-[#F3F4F6]">
              <tr>
                <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider">
                  Code
                </th>
                <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider">
                  Type
                </th>
                <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider">
                  Value
                </th>
                <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider">
                  Usage
                </th>
                <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider">
                  Status
                </th>
                <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider w-16 sm:w-20">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#DBDBDB]">
              {filteredPromoCodes.map((promo) => (
                <tr key={promo.id} className="hover:bg-gray-50 transition-colors duration-150">
                  <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                    <span className="text-xs sm:text-sm font-normal text-[#0E1628]">{promo.code}</span>
                  </td>
                  <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                    <span className="text-xs sm:text-sm font-normal text-[#8A9099]">{promo.type}</span>
                  </td>
                  <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                    <span className="text-xs sm:text-sm font-normal text-[#0E1628]">{promo.value}</span>
                  </td>
                  <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                    <span className="text-xs sm:text-sm font-normal text-[#8A9099]">{promo.usage}</span>
                  </td>
                  <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                    {getStatusBadge(promo.status)}
                  </td>
                  <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                    <span className="text-xs sm:text-sm text-[#55000C] cursor-pointer hover:text-[#440009]">Edit</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filteredPromoCodes.length > 0 && (
          <div className="mt-4 sm:mt-6">
            <div className="flex justify-center">
              <Pagination totalItems={filteredPromoCodes.length} itemsPerPage={10} />
            </div>
          </div>
        )}

        {/* Empty State */}
        {filteredPromoCodes.length === 0 && (
          <div className="text-center py-8 sm:py-12">
            <div className="text-gray-400 mb-2 text-sm">No promo codes found</div>
            <p className="text-xs sm:text-sm text-gray-500">
              {searchQuery || statusFilter !== 'all' 
                ? 'Try adjusting your search or filter'
                : 'Create your first promo code to get started'}
            </p>
          </div>
        )}
      </div>

      {/* Mobile Filters Modal */}
      <MobileFiltersModal 
        isOpen={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        statusFilters={statusFilters}
      />
    </div>
  );
};

export default DiscountPromoTab;